import { useEffect, useRef, useState } from 'react';

interface TipState {
  text: string;
  x: number;
  y: number;
  below: boolean;
  /** Ancrage horizontal quand on est près d'un bord. */
  anchor: 'center' | 'left' | 'right';
}

const DELAY_MS = 160;
const EDGE_PX = 190;

/**
 * Info-bulles stylées à l'échelle de l'app (Ben 17/07 : « les info-bulles pas
 * en bête mise en page navigateur — joli comme app »).
 *
 * Mécanisme : au survol d'un élément portant `title`, l'attribut est déplacé
 * en `data-tip` (ce qui SUPPRIME la bulle native de l'OS) et une bulle maison
 * est affichée près de l'élément. Aucun composant à modifier : tout `title=`
 * existant ou futur est automatiquement upgradé. La règle « éduquer l'humain,
 * confiance progressive » garde ainsi ses explications partout, en joli.
 */
export function AppTooltip(): JSX.Element | null {
  const [tip, setTip] = useState<TipState | null>(null);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    const clear = () => {
      if (timer.current != null) {
        window.clearTimeout(timer.current);
        timer.current = null;
      }
    };
    const hide = () => {
      clear();
      setTip(null);
    };

    function onOver(e: MouseEvent) {
      const target = e.target as Element | null;
      const el = target?.closest?.('[title], [data-tip]') as HTMLElement | null;
      if (!el) return;
      // Déplace title → data-tip (tue la bulle native). React peut remettre
      // `title` au re-render : on re-synchronise à chaque survol.
      const title = el.getAttribute('title');
      if (title) {
        el.setAttribute('data-tip', title);
        el.removeAttribute('title');
      }
      const text = el.getAttribute('data-tip');
      if (!text || !text.trim()) return;
      clear();
      timer.current = window.setTimeout(() => {
        const r = el.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const below = r.top < 80; // pas de place au-dessus → en dessous
        const anchor: TipState['anchor'] =
          cx < EDGE_PX ? 'left' : cx > window.innerWidth - EDGE_PX ? 'right' : 'center';
        setTip({
          text,
          x: anchor === 'left' ? r.left : anchor === 'right' ? r.right : cx,
          y: below ? r.bottom + 8 : r.top - 8,
          below,
          anchor,
        });
      }, DELAY_MS);
    }

    function onOut(e: MouseEvent) {
      const target = e.target as Element | null;
      if (target?.closest?.('[data-tip]')) hide();
    }

    document.addEventListener('mouseover', onOver);
    document.addEventListener('mouseout', onOut);
    document.addEventListener('mousedown', hide);
    document.addEventListener('scroll', hide, true);
    window.addEventListener('blur', hide);
    return () => {
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mouseout', onOut);
      document.removeEventListener('mousedown', hide);
      document.removeEventListener('scroll', hide, true);
      window.removeEventListener('blur', hide);
      clear();
    };
  }, []);

  if (!tip) return null;
  const translateX = tip.anchor === 'center' ? '-50%' : tip.anchor === 'right' ? '-100%' : '0';
  return (
    <div
      role="tooltip"
      className="fixed z-[100] pointer-events-none max-w-[360px] px-[10px] py-[7px] rounded-[8px] bg-slate-900/95 text-white text-[12px] leading-[16px] shadow-xl"
      style={{
        left: tip.x,
        top: tip.y,
        transform: `translate(${translateX}, ${tip.below ? '0' : '-100%'})`,
      }}
    >
      {tip.text}
    </div>
  );
}
